<template>
  <div class="chat-window">
    <!-- Header del chat -->
    <div class="chat-header">
      <div class="d-flex align-center pa-3">
        <!-- Botón volver (mobile) -->
        <v-btn
          v-if="showBack"
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          class="mr-1"
          @click="$emit('close')"
        />
        <!-- Avatar y nombre del contacto -->
        <v-avatar
          size="40"
          :color="avatarColor"
          class="mr-3"
        >
          <img 
            v-if="conversation.avatar" 
            :src="conversation.avatar" 
            :alt="conversation.patient_name"
          />
          <span v-else class="text-h6 font-weight-bold">
            {{ getInitials(conversation.patient_name) }}
          </span>
        </v-avatar>
        
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium">
            {{ conversation.patient_name }}
          </div>
          <div class="text-caption text-grey">
            {{ formatPhone(conversation.patient_phone) }}
          </div>
        </div>
        
        <!-- Acciones del header -->
        <div class="d-flex align-center">
          <!-- Estado del caso -->
          <v-chip
            v-if="caseChip"
            :color="caseChip.color"
            variant="tonal"
            size="small"
            class="mr-2"
          >
            <v-icon size="small" class="mr-1">{{ caseChip.icon }}</v-icon>
            {{ caseChip.text }}
          </v-chip>

          <!-- Status de conexión -->
          <v-chip
            :color="connectionStatus.color"
            variant="tonal"
            size="small"
            class="mr-2"
          >
            <v-icon size="small" class="mr-1">{{ connectionStatus.icon }}</v-icon>
            {{ connectionStatus.text }}
          </v-chip>
          
          <!-- Menú de acciones -->
          <v-menu>
            <template #activator="{ props }">
              <v-btn 
                icon="mdi-dots-vertical" 
                variant="text" 
                size="small" 
                v-bind="props"
              />
            </template>
            <v-list density="compact">
              <v-list-item @click="refreshMessages">
                <template #prepend>
                  <v-icon size="small">mdi-refresh</v-icon>
                </template>
                <v-list-item-title>Actualizar mensajes</v-list-item-title>
              </v-list-item>
              <v-list-item @click="exportChat">
                <template #prepend>
                  <v-icon size="small">mdi-download</v-icon>
                </template>
                <v-list-item-title>Exportar conversación</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewPatientInfo">
                <template #prepend>
                  <v-icon size="small">mdi-account-details</v-icon>
                </template>
                <v-list-item-title>Info del paciente</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item v-if="caseStatus !== 'closed'" @click="closeCase">
                <template #prepend>
                  <v-icon size="small" color="success">mdi-check-circle</v-icon>
                </template>
                <v-list-item-title>Marcar caso completado</v-list-item-title>
              </v-list-item>
              <v-list-item v-else @click="reopenCase">
                <template #prepend>
                  <v-icon size="small" color="warning">mdi-replay</v-icon>
                </template>
                <v-list-item-title>Reabrir caso</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="$emit('close')" class="text-error">
                <template #prepend>
                  <v-icon size="small" color="error">mdi-close</v-icon>
                </template>
                <v-list-item-title>Cerrar chat</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>
    
    <!-- Área de mensajes -->
    <div 
      ref="messagesContainer" 
      class="messages-area"
      @scroll="onScroll"
    >
      <!-- Loading de mensajes -->
      <div v-if="loading" class="loading-messages">
        <div class="d-flex justify-center align-center pa-4">
          <v-progress-circular indeterminate color="primary" size="24" />
          <span class="ml-2">Cargando mensajes...</span>
        </div>
      </div>

      <!-- Mensajes agrupados por fecha -->
      <div v-else-if="messages.length > 0" class="messages-list pa-3">
        <!-- Boton cargar mas -->
        <div v-if="hasMore" class="d-flex justify-center mb-3">
          <v-btn
            variant="tonal"
            size="small"
            :loading="loadingOlder"
            @click="$emit('load-older')"
          >
            <v-icon size="small" class="mr-1">mdi-arrow-up</v-icon>
            Cargar mensajes anteriores
          </v-btn>
        </div>
        <template v-for="(dayMessages, date) in messagesByDate" :key="date">
          <!-- Separador de fecha -->
          <div class="date-separator">
            <v-chip 
              size="small" 
              color="grey-lighten-4" 
              class="text-grey-darken-2"
              variant="flat"
            >
              {{ formatDateSeparator(date) }}
            </v-chip>
          </div>
          
          <!-- Mensajes del día -->
          <div v-for="message in dayMessages" :key="message.id">
            <MessageBubble 
              :message="message"
              :is-outgoing="message.direction === 'outgoing'"
              :patient-name="conversation.patient_name"
            />
          </div>
        </template>
      </div>
      
      <!-- Estado vacío -->
      <div v-else class="empty-messages">
        <div class="text-center pa-6">
          <v-icon size="64" color="grey-lighten-3">mdi-chat-outline</v-icon>
          <div class="text-body-1 text-grey mt-3">
            No hay mensajes en esta conversación
          </div>
          <div class="text-caption text-grey">
            Los mensajes aparecerán aquí
          </div>
        </div>
      </div>
    </div>
    
    <!-- Input para escribir mensajes -->
    <div class="message-input">
      <div class="d-flex align-center pa-3 bg-white">
        <!-- Campo de texto -->
        <v-textarea
          v-model="newMessage"
          placeholder="Escribe un mensaje..."
          variant="outlined"
          density="compact"
          rows="1"
          auto-grow
          max-rows="4"
          hide-details
          class="flex-grow-1 mr-2"
          :disabled="sending || !canSendMessages"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.enter.shift.exact="newMessage += '\n'"
        >
          <template #prepend-inner>
            <v-btn 
              icon="mdi-emoticon-outline" 
              variant="text" 
              size="small"
              @click="showEmojiPicker = !showEmojiPicker"
            />
          </template>
        </v-textarea>
        
        <!-- Botón de enviar -->
        <v-btn
          :disabled="!newMessage.trim() || sending || !canSendMessages"
          :loading="sending"
          color="success"
          icon="mdi-send"
          size="small"
          @click="sendMessage"
        />
      </div>
      
      <!-- Status de ventana de 24 horas -->
      <div v-if="windowStatus" class="window-status px-3 pb-2">
        <div class="d-flex align-center text-caption">
          <v-icon 
            :color="windowStatus.isOpen ? 'success' : 'warning'" 
            size="small" 
            class="mr-1"
          >
            {{ windowStatus.isOpen ? 'mdi-clock-check' : 'mdi-clock-alert' }}
          </v-icon>
          <span :class="windowStatus.isOpen ? 'text-success' : 'text-warning'">
            {{ windowStatus.message }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Picker de emojis (simple) -->
    <div v-if="showEmojiPicker" class="emoji-picker">
      <div class="d-flex flex-wrap pa-2 bg-white border">
        <v-btn
          v-for="emoji in commonEmojis"
          :key="emoji"
          variant="text"
          size="small"
          class="ma-1"
          @click="addEmoji(emoji)"
        >
          {{ emoji }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import MessageBubble from './MessageBubble.vue'
import { useChatStore } from '@/stores/chat'

const chatStore = useChatStore()

const props = defineProps({
  conversation: {
    type: Object,
    required: true
  },
  messages: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  sending: {
    type: Boolean,
    default: false
  },
  showBack: {
    type: Boolean,
    default: false
  },
  hasMore: {
    type: Boolean,
    default: false
  },
  loadingOlder: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['send-message', 'close', 'load-older'])

// State
const messagesContainer = ref(null)
const newMessage = ref('')
const showEmojiPicker = ref(false)

// Computed
const avatarColor = computed(() => {
  const colors = [
    'red', 'pink', 'purple', 'deep-purple', 'indigo', 'blue',
    'light-blue', 'cyan', 'teal', 'green', 'light-green',
    'lime', 'amber', 'orange', 'deep-orange', 'brown', 'blue-grey'
  ]
  
  const name = props.conversation.patient_name || 'Unknown'
  const hash = name.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)
  
  return colors[Math.abs(hash) % colors.length]
})

const messagesByDate = computed(() => {
  const grouped = {}
  props.messages.forEach(message => {
    const date = new Date(message.created_at).toDateString()
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(message)
  })
  
  return grouped
})

const connectionStatus = computed(() => {
  // Simulado - en producción usar lógica real de conexión
  return {
    color: 'success',
    icon: 'mdi-wifi',
    text: 'En línea'
  }
})

const caseStatus = computed(() => chatStore.currentCaseStatus)

const caseChip = computed(() => {
  switch (caseStatus.value) {
    case 'pending':
      return { color: 'error', icon: 'mdi-alert-circle', text: 'Pendiente' }
    case 'in_progress':
      return { color: 'warning', icon: 'mdi-progress-clock', text: 'En progreso' }
    case 'closed':
      return { color: 'success', icon: 'mdi-check-circle', text: 'Atendido' }
    default:
      return null
  }
})

const closeCase = async () => {
  await chatStore.closeCurrentCase()
}

const reopenCase = async () => {
  await chatStore.reopenCurrentCase()
}

const windowStatus = computed(() => {
  // La ventana de 24h se abre cuando:
  // 1. El paciente envía un mensaje (incoming)
  // 2. La clínica envía un template aprobado
  
  // Buscar el último mensaje del PACIENTE (incoming)
  const lastIncoming = props.messages
    .filter(m => m.direction === 'incoming')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
  
  // Buscar el último template enviado (outgoing con twilio_sid que empiece con HX)
  const lastTemplate = props.messages
    .filter(m => m.direction === 'outgoing' && m.twilio_sid?.startsWith('HX'))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
  
  // Determinar cuál es más reciente para abrir la ventana
  let lastRelevantMessage = null
  let triggerType = null
  
  if (lastIncoming && lastTemplate) {
    // Si hay ambos, usar el más reciente
    const incomingTime = new Date(lastIncoming.created_at)
    const templateTime = new Date(lastTemplate.created_at)
    if (incomingTime > templateTime) {
      lastRelevantMessage = lastIncoming
      triggerType = 'patient_message'
    } else {
      lastRelevantMessage = lastTemplate
      triggerType = 'template_sent'
    }
  } else if (lastIncoming) {
    lastRelevantMessage = lastIncoming
    triggerType = 'patient_message'
  } else if (lastTemplate) {
    lastRelevantMessage = lastTemplate
    triggerType = 'template_sent'
  }
  
  if (!lastRelevantMessage) {
    return {
      isOpen: false,
      message: 'Sin conversación activa - El paciente debe escribir primero',
      hoursRemaining: 0
    }
  }
  
  const lastTime = new Date(lastRelevantMessage.created_at)
  const now = new Date()
  const hoursElapsed = (now - lastTime) / (1000 * 60 * 60)
  const hoursRemaining = Math.max(0, 24 - hoursElapsed)
  const isOpen = hoursElapsed < 24
  
  if (isOpen) {
    const remainingText = hoursRemaining > 1 
      ? `${Math.floor(hoursRemaining)}h ${Math.floor((hoursRemaining % 1) * 60)}min`
      : `${Math.floor(hoursRemaining * 60)}min`
    
    const triggerText = triggerType === 'patient_message' 
      ? 'Paciente escribió' 
      : 'Template enviado'
    
    return {
      isOpen: true,
      message: `Ventana activa (${triggerText}) - ${remainingText} restantes`,
      hoursRemaining: hoursRemaining
    }
  } else {
    return {
      isOpen: false,
      message: 'Ventana cerrada - Espera mensaje del paciente o envía template',
      hoursRemaining: 0
    }
  }
})

const canSendMessages = computed(() => {
  return windowStatus.value?.isOpen !== false
})

const commonEmojis = [
  '😊', '😂', '❤️', '👍', '👎', '🙏', '🤝', '👏', '🎉', '✅',
  '❌', '⚠️', '📅', '🕐', '📞', '💊', '🦷', '😷', '🏥', '👩‍⚕️'
]

// Methods
const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()
}

const formatPhone = (phone) => {
  if (!phone) return ''
  // Formatear teléfono: +34 123 456 789
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.startsWith('34') && cleaned.length === 11) {
    return `+34 ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8)}`
  }
  return phone
}

const formatDateSeparator = (dateString) => {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  
  if (date.toDateString() === today.toDateString()) {
    return 'Hoy'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Ayer'
  } else {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    })
  }
}

const sendMessage = () => {
  if (!newMessage.value.trim() || props.sending) return
  
  emit('send-message', newMessage.value.trim())
  newMessage.value = ''
  showEmojiPicker.value = false
}

const addEmoji = (emoji) => {
  newMessage.value += emoji
  showEmojiPicker.value = false
}

const scrollToBottom = () => {
  if (messagesContainer.value) {
    nextTick(() => {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    })
  }
}

const onScroll = () => {
  // TODO: Implementar carga de mensajes anteriores al hacer scroll hacia arriba
}

const refreshMessages = () => {
}

const exportChat = () => {
  const chatContent = props.messages.map(msg => 
    `[${new Date(msg.created_at).toLocaleString('es-ES')}] ${
      msg.direction === 'outgoing' ? 'Enviado' : 'Recibido'
    }: ${msg.content}`
  ).join('\n')
  
  const blob = new Blob([chatContent], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `chat_${props.conversation.patient_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`
  a.click()
  window.URL.revokeObjectURL(url)
}

const viewPatientInfo = () => {
}

// Watchers
watch(() => props.messages.length, () => {
  scrollToBottom()
})

// Lifecycle
onMounted(() => {
  scrollToBottom()
})
</script>

<style scoped>
.chat-window {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #efeae2;
}

.chat-header {
  background: #ededed;
  border-bottom: 1px solid #d1d7db;
  flex-shrink: 0;
}

.messages-area {
  flex: 1;
  overflow-y: auto;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="whatsapp-bg" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.03"/></pattern></defs><rect width="100" height="100" fill="url(%23whatsapp-bg)"/></svg>');
}

.messages-list {
  min-height: 100%;
}

.date-separator {
  display: flex;
  justify-content: center;
  margin: 16px 0;
}

.loading-messages {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.empty-messages {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.message-input {
  background: #f0f0f0;
  border-top: 1px solid #d1d7db;
  flex-shrink: 0;
}

.window-status {
  background: #f9f9f9;
  border-top: 1px solid #e0e0e0;
}

.emoji-picker {
  position: absolute;
  bottom: 100px;
  left: 20px;
  z-index: 10;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 300px;
}

/* Scrollbar personalizada */
.messages-area::-webkit-scrollbar {
  width: 6px;
}

.messages-area::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.messages-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.messages-area::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>